/**
 * Tenant list — the console's home.
 *
 * Summary before detail: the counts row answers "is anything wrong" before the
 * table answers "with what". `domainPending` is surfaced prominently because it is
 * the single most likely reason a freshly created tenant appears broken: the row
 * exists, the certificate does not.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Card, EmptyState, Pill, Spinner, Stat } from '../../shared/ui';
import { PageHeader } from '../../shared/layout/WorkspaceShell';
import { tenantsApi, type Tenant } from '../api/tenants';

export default function TenantList() {
  const queryClient = useQueryClient();

  const { data: tenants, isLoading, error } = useQuery({
    queryKey: ['tenants'],
    queryFn: tenantsApi.list,
  });

  const suspend = useMutation({
    mutationFn: tenantsApi.suspend,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenants'] }),
  });

  const summary = useMemo(() => {
    const list: Tenant[] = tenants ?? [];
    return {
      total: list.length,
      active: list.filter((t) => t.status === 'active').length,
      pending: list.filter((t) => t.status === 'planned').length,
      suspended: list.filter((t) => t.status === 'suspended').length,
    };
  }, [tenants]);

  if (isLoading) return <Spinner label="Loading tenants…" />;

  if (error) {
    return (
      <Banner tone="danger">
        <strong>Could not load tenants.</strong> The tenancy service did not respond.
        Nothing has changed — retry, and check the LMS is reachable.
      </Banner>
    );
  }

  return (
    <>
      {/* The page states itself, the same way every admin surface does — the sidebar says
          where you are, not what this page is for. */}
      <PageHeader
        title="Tenants"
        description="Every academy on this platform. One tenant is one domain, one catalogue and one set of branding."
        actions={
          <Link to="/tenants/new" className="ui-btn" data-variant="primary">
            Add tenant
          </Link>
        }
      />

      <div className="console__stats">
        <Stat label="Tenants" value={summary.total} />
        <Stat label="Serving" value={summary.active} />
        <Stat label="Awaiting certificate" value={summary.pending} tone={summary.pending ? 'warning' : undefined} />
        <Stat label="Suspended" value={summary.suspended} tone={summary.suspended ? 'danger' : undefined} />
      </div>

      {summary.pending > 0 && (
        <Banner tone="warning">
          {summary.pending === 1 ? 'One tenant is' : `${summary.pending} tenants are`} registered
          but not yet serving HTTPS. Certificates are issued on first request — if this persists,
          check that DNS points at the cluster.
        </Banner>
      )}

      {/* Untitled: the page header above already names it, and a card repeating its own
          page's title is the kind of duplication that makes a console feel bureaucratic. */}
      <Card>
        {tenants && tenants.length > 0 ? (
          <div className="ui-table-scroll">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Domain</th>
                  <th>Organisations</th>
                  <th>Courses</th>
                  <th>Learners</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <Row
                    key={tenant.slug}
                    tenant={tenant}
                    onSuspend={() => suspend.mutate(tenant.slug)}
                    suspending={suspend.isPending && suspend.variables === tenant.slug}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="No tenants yet"
            body="A tenant is one academy: its own domain, catalogue and branding."
            action={
              <Link to="/tenants/new" className="ui-btn" data-variant="primary">
                Add the first tenant
              </Link>
            }
          />
        )}
      </Card>
    </>
  );
}

function Row({
  tenant,
  onSuspend,
  suspending,
}: {
  tenant: Tenant;
  onSuspend: () => void;
  suspending: boolean;
}) {
  return (
    <tr>
      <td>
        <Link to={`/tenants/${tenant.slug}`}>{tenant.name}</Link>
        {tenant.is_primary && <span className="console__primary"> primary</span>}
      </td>
      <td className="mono">
        {tenant.domains.site}
        {tenant.status === 'planned' && (
          <>
            {' '}
            <Pill tone="planned">no TLS</Pill>
          </>
        )}
      </td>
      <td className="mono">{tenant.orgs.join(', ')}</td>
      <td className="num">{'—'}</td>
      <td className="num">{'—'}</td>
      <td>
        <Pill tone={tenant.status}>{tenant.status}</Pill>
      </td>
      <td>
        {tenant.status === 'active' && !tenant.is_primary && (
          <Button variant="danger" busy={suspending} onClick={onSuspend}>
            Suspend
          </Button>
        )}
      </td>
    </tr>
  );
}
